import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LegacyRedirect from "@/components/LegacyRedirect";
import { Suspense, lazy } from "react";
import { PageTracker } from "@/components/PageTracker";
import { AnalyticsDashboard } from "@/components/AnalyticsDashboard";
import { useMetricoolTracking } from "@/hooks/useMetricoolTracking";
import { ChatWidget } from "@/components/ChatWidget";
import { CanonicalUrl } from "@/components/CanonicalUrl";
import { RouteNormalizer } from "@/components/RouteNormalizer";
import ErrorBoundary from "@/components/ErrorBoundary";
import { CookieConsent } from "@/components/shared/CookieConsent";
import { StickyMobileCTA } from "@/components/shared/StickyMobileCTA";
import { ScrollTriggerCTA } from "@/components/shared/ScrollTriggerCTA";
import ExitIntentPopup from "@/components/shared/ExitIntentPopup";
import ScrollToTop from "@/components/shared/ScrollToTop";


// Eagerly load Index for fastest homepage rendering
import Index from "./pages/Index";

// Lazy load all other pages for code splitting
const Product = lazy(() => import("./pages/Product"));
const Features = lazy(() => import("./pages/Features"));
const GovernanceManager = lazy(() => import("./pages/features/GovernanceManager"));
const DocumentsRegisterRepository = lazy(() => import("./pages/features/DocumentsRegisterRepository"));
const TASBuilder = lazy(() => import("./pages/features/TASBuilder"));
const ComplyBot = lazy(() => import("./pages/features/ComplyBot"));
const TrainersMatrix = lazy(() => import("./pages/features/TrainersMatrix"));
const IndustryConsultation = lazy(() => import("./pages/features/IndustryConsultation"));
const Registers = lazy(() => import("./pages/features/Registers"));
const Blog = lazy(() => import("./pages/Blog"));
const Resources = lazy(() => import("./pages/Resources"));
const DemoLibrary = lazy(() => import("./pages/DemoLibrary"));
const RTOStandards2025 = lazy(() => import("./pages/blog/RTOStandards2025"));
const RTOAuditChecklist = lazy(() => import("./pages/blog/RTOAuditChecklist"));
const RTOGovernanceEvidence = lazy(() => import("./pages/blog/RTOGovernanceEvidence"));
const RTOAssessmentValidationRisk = lazy(() => import("./pages/blog/RTOAssessmentValidationRisk"));
const TrainerAssessorAuditRisk = lazy(() => import("./pages/blog/TrainerAssessorAuditRisk"));
const RTOAssessmentIntegrity = lazy(() => import("./pages/blog/RTOAssessmentIntegrity"));
const RTOContinuousImprovementEvidence = lazy(() => import("./pages/blog/RTOContinuousImprovementEvidence"));
const StudentSupportAuditEvidence = lazy(() => import("./pages/blog/StudentSupportAuditEvidence"));
const RTOWorkforceCompetencyMatrix = lazy(() => import("./pages/blog/RTOWorkforceCompetencyMatrix"));
const RTOAuditPackBuilder = lazy(() => import("./pages/blog/RTOAuditPackBuilder"));
const RTODocumentControlAudit = lazy(() => import("./pages/blog/RTODocumentControlAudit"));
const RTOGovernanceRegister = lazy(() => import("./pages/blog/RTOGovernanceRegister"));
const RTOStudentSupportEvidence = lazy(() => import("./pages/blog/RTOStudentSupportEvidence"));
const RTOMarketingCompliance = lazy(() => import("./pages/blog/RTOMarketingCompliance"));
const RTOTASCompliance = lazy(() => import("./pages/blog/RTOTASCompliance"));
const RTODeliveryModes = lazy(() => import("./pages/blog/RTODeliveryModes"));
const RTOVolumeOfLearning = lazy(() => import("./pages/blog/RTOVolumeOfLearning"));
const RTORPLCreditTransfer = lazy(() => import("./pages/blog/RTORPLCreditTransfer"));
const RTODiversityInclusion = lazy(() => import("./pages/blog/RTODiversityInclusion"));
const RTOFacilitiesResources = lazy(() => import("./pages/blog/RTOFacilitiesResources"));
const NSWSmartSkilledFunding2025 = lazy(() => import("./pages/blog/NSWSmartSkilledFunding2025"));
const AuditReadyCompliance = lazy(() => import("./pages/blog/AuditReadyCompliance"));
const SpreadsheetComplianceRisk = lazy(() => import("./pages/blog/SpreadsheetComplianceRisk"));
const PreparingStandardsChanges = lazy(() => import("./pages/blog/PreparingStandardsChanges"));
const WhatIsRTOComplianceManagement = lazy(() => import("./pages/blog/WhatIsRTOComplianceManagement"));
const WhatDoesAuditReadyMean = lazy(() => import("./pages/blog/WhatDoesAuditReadyMean"));
const HowRTOsPrepareASQAAudit = lazy(() => import("./pages/blog/HowRTOsPrepareASQAAudit"));
const WhatEvidenceASQAAsksForAudit = lazy(() => import("./pages/blog/WhatEvidenceASQAAsksForAudit"));
const RTOEvidenceVersionControl = lazy(() => import("./pages/blog/RTOEvidenceVersionControl"));
const WhatIsComplianceRegisterSpreadsheetsFail = lazy(() => import("./pages/blog/WhatIsComplianceRegisterSpreadsheetsFail"));
const WhatSoftwareRTOsUseCompliance = lazy(() => import("./pages/blog/WhatSoftwareRTOsUseCompliance"));
const CanComplianceBeAutomatedRTOs = lazy(() => import("./pages/blog/CanComplianceBeAutomatedRTOs"));
const RTOComplianceSoftwareFeatures = lazy(() => import("./pages/blog/RTOComplianceSoftwareFeatures"));
const VsRTOSafe = lazy(() => import("./pages/vs/RTOSafe"));
const VsOctossure = lazy(() => import("./pages/vs/Octossure"));
const VsESkilled = lazy(() => import("./pages/vs/ESkilled"));
const RTOComplianceSoftware = lazy(() => import("./pages/RTOComplianceSoftware"));
const SMS = lazy(() => import("./pages/SMS"));
const SharePointAlternativeRTOCompliance = lazy(() => import("./pages/blog/SharePointAlternativeRTOCompliance"));
const RTOTrackComplianceMultipleStandards = lazy(() => import("./pages/blog/RTOTrackComplianceMultipleStandards"));
const WhoIsResponsibleForRTOCompliance = lazy(() => import("./pages/blog/WhoIsResponsibleForRTOCompliance"));
const RTOComplianceTaskAssignmentTracking = lazy(() => import("./pages/blog/RTOComplianceTaskAssignmentTracking"));
const RTOGovernanceMeetingsCompliance = lazy(() => import("./pages/blog/RTOGovernanceMeetingsCompliance"));
const RTOContinuousImprovementAdmin = lazy(() => import("./pages/blog/RTOContinuousImprovementAdmin"));
const StudentSupportComplianceEvidence = lazy(() => import("./pages/blog/StudentSupportComplianceEvidence"));
const RTOTrainerAssessorCompetencyEvidence = lazy(() => import("./pages/blog/RTOTrainerAssessorCompetencyEvidence"));
const AssessmentValidationASQAAuditFindings = lazy(() => import("./pages/blog/AssessmentValidationASQAAuditFindings"));
const CommonComplianceBreachesRTOAudits = lazy(() => import("./pages/blog/CommonComplianceBreachesRTOAudits"));
const CEORTOComplianceVisibility = lazy(() => import("./pages/blog/CEORTOComplianceVisibility"));
const RTOAuditPitfalls = lazy(() => import("./pages/blog/RTOAuditPitfalls"));
const RTOSelfAssuranceGuide = lazy(() => import("./pages/blog/RTOSelfAssuranceGuide"));
const RTOComplianceCalendar2025 = lazy(() => import("./pages/blog/RTOComplianceCalendar2025"));
const NationalSkillsAgreement70Percent = lazy(() => import("./pages/blog/NationalSkillsAgreement70Percent"));
const RTOSActuallyWinning2025_26 = lazy(() => import("./pages/blog/RTOSActuallyWinning2025_26"));
const RTOSellOffSectorCrisis = lazy(() => import("./pages/blog/RTOSellOffSectorCrisis"));
const RTOSAsqaCancelsGovernanceNotTafe = lazy(() => import("./pages/blog/RTOSAsqaCancelsGovernanceNotTafe"));
const PrivateRTOSqueezeNextThreeYears = lazy(() => import("./pages/blog/PrivateRTOSqueezeNextThreeYears"));
const ASQAAudit90Days = lazy(() => import("./pages/blog/ASQAAudit90Days"));
const RTOComplianceSoftwareAuditReady = lazy(() => import("./pages/blog/RTOComplianceSoftwareAuditReady"));
const HowComplyHubAutomatesRTOCompliance = lazy(() => import("./pages/blog/HowComplyHubAutomatesRTOCompliance"));
const WhatIsComplyHub = lazy(() => import("./pages/blog/WhatIsComplyHub"));
const StandardsForRTOs2025ComplyHub = lazy(() => import("./pages/blog/StandardsForRTOs2025ComplyHub"));
const RtoComplianceSoftwareGuide = lazy(() => import("./pages/blog/RtoComplianceSoftwareGuide"));
const RtoComplianceAiTools = lazy(() => import("./pages/blog/RtoComplianceAiTools"));
const AsqaAuditPreparation = lazy(() => import("./pages/blog/AsqaAuditPreparation"));
const RtoComplianceFaq = lazy(() => import("./pages/blog/RtoComplianceFaq"));
const RtoComplianceFaqPage = lazy(() => import("./pages/RtoComplianceFaqPage"));
const RTOComplianceSoftwareVsSMS = lazy(() => import("./pages/blog/RTOComplianceSoftwareVsSMS"));
const RTOComplianceSoftwareVsLMS = lazy(() => import("./pages/blog/RTOComplianceSoftwareVsLMS"));
const GovernanceScorecard = lazy(() => import("./pages/GovernanceScorecard"));
const GovernanceScorecardStart = lazy(() => import("./pages/GovernanceScorecardStart"));
const GovernanceScorecardThankYou = lazy(() => import("./pages/GovernanceScorecardThankYou"));
const EightCDScorecard = lazy(() => import("./pages/EightCDScorecard"));
const Auditors = lazy(() => import("./pages/Auditors"));
const Compliance = lazy(() => import("./pages/Compliance"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Demo = lazy(() => import("./pages/Demo"));
const ExecutiveSummary = lazy(() => import("./pages/ExecutiveSummary"));
const Auth = lazy(() => import("./pages/Auth"));
const Analytics = lazy(() => import("./pages/Analytics"));
const SalesPack = lazy(() => import("./pages/SalesPack"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const AcceptableUsePolicy = lazy(() => import("./pages/AcceptableUsePolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const DataProcessingAddendum = lazy(() => import("./pages/DataProcessingAddendum"));
const Podcast = lazy(() => import("./pages/Podcast"));
const EpisodePage = lazy(() => import("./pages/podcast/EpisodePage"));
const MaturityCheck = lazy(() => import("./pages/MaturityCheck"));
const MaturityCheckReport = lazy(() => import("./pages/MaturityCheckReport"));
const MaturityCheckIntro = lazy(() => import("./pages/MaturityCheckIntro"));
const MaturityCheckComplete = lazy(() => import("./pages/MaturityCheckComplete"));
const DownloadConfirmed = lazy(() => import("./pages/DownloadConfirmed"));
const DemoConfirmed = lazy(() => import("./pages/DemoConfirmed"));
const TrialConfirmed = lazy(() => import("./pages/TrialConfirmed"));
const Book = lazy(() => import("./pages/Book"));
const Workbook = lazy(() => import("./pages/Workbook"));

const Checkout = lazy(() => import("./pages/Checkout"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const WorkshopOffer = lazy(() => import("./pages/WorkshopOffer"));
const GovernanceShiftDiagrams = lazy(() => import("./pages/GovernanceShiftDiagrams"));
const GovernanceShiftDiagramsDownload = lazy(() => import("./pages/GovernanceShiftDiagramsDownload"));
const IntentRedirect = lazy(() => import("./components/IntentRedirect"));
const StartFreeTrial = lazy(() => import("./pages/StartFreeTrial"));
const TrialSignup = lazy(() => import("./pages/TrialSignup"));

const queryClient = new QueryClient();

// Minimal loading fallback for code-split routes
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="animate-pulse text-ch-acai/50">Loading...</div>
  </div>
);

const App = () => {
  useMetricoolTracking();
  
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ErrorBoundary>
          <PageTracker />
          <AnalyticsDashboard />
          <ChatWidget />
          <ScrollToTop />
          <CanonicalUrl />
          <RouteNormalizer />
          <CookieConsent />
          <StickyMobileCTA />
          <ScrollTriggerCTA />
          <ExitIntentPopup />
          
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Core Pages */}
              <Route path="/" element={<Index />} />
              <Route path="/product" element={<Product />} />
              <Route path="/features" element={<Features />} />
              <Route path="/features/governance-meeting-manager" element={<GovernanceManager />} />
              <Route path="/features/documents-register-repository" element={<DocumentsRegisterRepository />} />
              <Route path="/features/tas-builder" element={<TASBuilder />} />
              <Route path="/features/complybot" element={<ComplyBot />} />
              <Route path="/features/trainers-matrix" element={<TrainersMatrix />} />
              <Route path="/features/industry-consultation" element={<IndustryConsultation />} />
              <Route path="/registers" element={<Registers />} />
              <Route path="/rto-compliance-software" element={<RTOComplianceSoftware />} />
              <Route path="/sms" element={<SMS />} />
              <Route path="/start-free-trial" element={<StartFreeTrial />} />
              <Route path="/trial-signup" element={<TrialSignup />} />
              
              {/* Resources & Blog */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/demo-library" element={<DemoLibrary />} />
              <Route path="/podcast" element={<Podcast />} />
              <Route path="/podcast/:slug" element={<EpisodePage />} />
              
              {/* Blog Articles */}
              <Route path="/rto-standards-2025" element={<RTOStandards2025 />} />
              <Route path="/rto-audit-checklist" element={<RTOAuditChecklist />} />
              <Route path="/rto-governance-evidence" element={<RTOGovernanceEvidence />} />
              <Route path="/rto-assessment-validation-risk" element={<RTOAssessmentValidationRisk />} />
              <Route path="/trainer-assessor-audit-risk" element={<TrainerAssessorAuditRisk />} />
              <Route path="/rto-assessment-integrity" element={<RTOAssessmentIntegrity />} />
              <Route path="/rto-continuous-improvement-evidence" element={<RTOContinuousImprovementEvidence />} />
              <Route path="/student-support-audit-evidence" element={<StudentSupportAuditEvidence />} />
              <Route path="/rto-workforce-competency-matrix" element={<RTOWorkforceCompetencyMatrix />} />
              <Route path="/rto-audit-pack-builder" element={<RTOAuditPackBuilder />} />
              <Route path="/rto-document-control-audit" element={<RTODocumentControlAudit />} />
              <Route path="/rto-governance-register" element={<RTOGovernanceRegister />} />
              <Route path="/rto-student-support-evidence" element={<RTOStudentSupportEvidence />} />
              <Route path="/rto-marketing-compliance" element={<RTOMarketingCompliance />} />
              <Route path="/rto-tas-compliance" element={<RTOTASCompliance />} />
              <Route path="/rto-delivery-modes" element={<RTODeliveryModes />} />
              <Route path="/rto-volume-of-learning" element={<RTOVolumeOfLearning />} />
              <Route path="/rto-rpl-credit-transfer-evidence" element={<RTORPLCreditTransfer />} />
              <Route path="/rto-diversity-inclusion-evidence" element={<RTODiversityInclusion />} />
              <Route path="/rto-facilities-resources-evidence" element={<RTOFacilitiesResources />} />
              <Route path="/nsw-smart-skilled-funding-2025" element={<NSWSmartSkilledFunding2025 />} />
              <Route path="/audit-ready-compliance" element={<AuditReadyCompliance />} />
              <Route path="/spreadsheet-compliance-risk" element={<SpreadsheetComplianceRisk />} />
              <Route path="/preparing-standards-changes" element={<PreparingStandardsChanges />} />
              <Route path="/what-is-rto-compliance-management" element={<WhatIsRTOComplianceManagement />} />
              <Route path="/what-does-audit-ready-mean" element={<WhatDoesAuditReadyMean />} />
              <Route path="/how-rtos-prepare-asqa-audit" element={<HowRTOsPrepareASQAAudit />} />
              <Route path="/what-evidence-asqa-asks-for-audit" element={<WhatEvidenceASQAAsksForAudit />} />
              <Route path="/rto-evidence-version-control" element={<RTOEvidenceVersionControl />} />
              <Route path="/what-is-compliance-register-spreadsheets-fail" element={<WhatIsComplianceRegisterSpreadsheetsFail />} />
              <Route path="/what-software-rtos-use-compliance" element={<WhatSoftwareRTOsUseCompliance />} />
              <Route path="/can-compliance-be-automated-rtos" element={<CanComplianceBeAutomatedRTOs />} />
              <Route path="/rto-compliance-software-features" element={<RTOComplianceSoftwareFeatures />} />
              <Route path="/sharepoint-alternative-rto-compliance" element={<SharePointAlternativeRTOCompliance />} />
              <Route path="/rto-track-compliance-multiple-standards" element={<RTOTrackComplianceMultipleStandards />} />
              <Route path="/who-is-responsible-for-rto-compliance" element={<WhoIsResponsibleForRTOCompliance />} />
              <Route path="/rto-compliance-task-assignment-tracking" element={<RTOComplianceTaskAssignmentTracking />} />
              <Route path="/rto-governance-meetings-compliance" element={<RTOGovernanceMeetingsCompliance />} />
              <Route path="/rto-continuous-improvement-admin" element={<RTOContinuousImprovementAdmin />} />
              <Route path="/student-support-compliance-evidence" element={<StudentSupportComplianceEvidence />} />
              <Route path="/rto-trainer-assessor-competency-evidence" element={<RTOTrainerAssessorCompetencyEvidence />} />
              <Route path="/assessment-validation-asqa-audit-findings" element={<AssessmentValidationASQAAuditFindings />} />
              <Route path="/common-compliance-breaches-rto-audits" element={<CommonComplianceBreachesRTOAudits />} />
              <Route path="/ceo-rto-compliance-visibility" element={<CEORTOComplianceVisibility />} />
              <Route path="/rto-audit-pitfalls-and-avoid-them" element={<RTOAuditPitfalls />} />
              <Route path="/rto-self-assurance-guide" element={<RTOSelfAssuranceGuide />} />
              <Route path="/rto-compliance-calendar-2025" element={<RTOComplianceCalendar2025 />} />
              <Route path="/national-skills-agreement-70-percent-private-rto-funding" element={<NationalSkillsAgreement70Percent />} />
              <Route path="/rtos-actually-winning-2025-26" element={<RTOSActuallyWinning2025_26 />} />
              <Route path="/rto-sell-off-sector-sold-liquidated-deregistered" element={<RTOSellOffSectorCrisis />} />
              <Route path="/rtos-asqa-cancels-governance-not-tafe" element={<RTOSAsqaCancelsGovernanceNotTafe />} />
              <Route path="/private-rto-squeeze-next-three-years" element={<PrivateRTOSqueezeNextThreeYears />} />
              <Route path="/asqa-audit-90-days" element={<ASQAAudit90Days />} />
              <Route path="/rto-compliance-software-audit-ready-2025" element={<RTOComplianceSoftwareAuditReady />} />
              <Route path="/how-complyhub-automates-rto-compliance" element={<HowComplyHubAutomatesRTOCompliance />} />
              <Route path="/what-is-complyhub" element={<Navigate to="/blog/what-is-complyhub" replace />} />
              <Route path="/standards-for-rtos-2025-complyhub" element={<Navigate to="/blog/standards-for-rtos-2025-complyhub" replace />} />
              <Route path="/blog/what-is-complyhub" element={<WhatIsComplyHub />} />
              <Route path="/blog/standards-for-rtos-2025-complyhub" element={<StandardsForRTOs2025ComplyHub />} />
              <Route path="/rto-compliance-software-guide" element={<Navigate to="/blog/rto-compliance-software-guide" replace />} />
              <Route path="/blog/rto-compliance-software-guide" element={<RtoComplianceSoftwareGuide />} />
              <Route path="/rto-compliance-ai-tools" element={<Navigate to="/blog/rto-compliance-ai-tools" replace />} />
              <Route path="/blog/rto-compliance-ai-tools" element={<RtoComplianceAiTools />} />
              <Route path="/asqa-audit-preparation" element={<Navigate to="/blog/asqa-audit-preparation" replace />} />
              <Route path="/blog/asqa-audit-preparation" element={<AsqaAuditPreparation />} />
              <Route path="/rto-compliance-faq" element={<Navigate to="/blog/rto-compliance-faq" replace />} />
              <Route path="/blog/rto-compliance-faq" element={<RtoComplianceFaq />} />
              <Route path="/resources/rto-compliance-faq" element={<RtoComplianceFaqPage />} />
              <Route path="/rto-compliance-software-vs-student-management-systems" element={<Navigate to="/blog/rto-compliance-software-vs-student-management-systems" replace />} />
              <Route path="/rto-compliance-software-vs-learning-management-system" element={<Navigate to="/blog/rto-compliance-software-vs-learning-management-system" replace />} />
              <Route path="/blog/rto-compliance-software-vs-student-management-systems" element={<RTOComplianceSoftwareVsSMS />} />
              <Route path="/blog/rto-compliance-software-vs-learning-management-system" element={<RTOComplianceSoftwareVsLMS />} />

              {/* Competitor comparison pages */}
              <Route path="/vs/rtosafe" element={<VsRTOSafe />} />
              <Route path="/vs/octossure" element={<VsOctossure />} />
              <Route path="/vs/eskilled" element={<VsESkilled />} />
              
              {/* Main Navigation Pages */}
              <Route path="/auditors" element={<Auditors />} />
              <Route path="/audit-readiness" element={<Navigate to="/auditors" replace />} />
              <Route path="/compliance" element={<Compliance />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/demo" element={<Demo />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/executive-summary" element={<ExecutiveSummary />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/sales-pack" element={<SalesPack />} />
              <Route path="/book" element={<Book />} />
              <Route path="/workbook" element={<Workbook />} />
              
              <Route path="/governance-scorecard" element={<GovernanceScorecard />} />
              <Route path="/governance-scorecard/start" element={<GovernanceScorecardStart />} />
              <Route path="/governance-scorecard/thank-you" element={<GovernanceScorecardThankYou />} />
              <Route path="/8CD-Scorecard" element={<EightCDScorecard />} />
              <Route path="/8cd-scorecard" element={<Navigate to="/8CD-Scorecard" replace />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/workshop-offer" element={<WorkshopOffer />} />
              <Route path="/governance-shift-diagrams" element={<GovernanceShiftDiagrams />} />
              <Route path="/governance-shift-diagrams/download" element={<GovernanceShiftDiagramsDownload />} />
              <Route path="/checkout/return" element={<CheckoutReturn />} />
              <Route path="/compliance-scorecard" element={<MaturityCheckIntro />} />
              <Route path="/compliance-check" element={<LegacyRedirect to="/compliance-scorecard" />} />
              <Route path="/download-confirmed/:resourceName" element={<DownloadConfirmed />} />
              <Route path="/demo-confirmed" element={<DemoConfirmed />} />
              <Route path="/trial-confirmed" element={<TrialConfirmed />} />
              <Route path="/compliance-scorecard/start" element={<MaturityCheck />} />
              <Route path="/compliance-scorecard/complete" element={<MaturityCheckComplete />} />
              <Route path="/compliance-scorecard/report/:id" element={<MaturityCheckReport />} />
              <Route path="/maturity-check" element={<LegacyRedirect to="/compliance-scorecard/start" />} />
              <Route path="/maturity-check/complete" element={<LegacyRedirect to="/compliance-scorecard" />} />
              <Route path="/maturity-check/report/:id" element={<LegacyRedirect to="/compliance-scorecard/report/:id" />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/acceptable-use-policy" element={<AcceptableUsePolicy />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/data-processing-addendum" element={<DataProcessingAddendum />} />
              
              {/* Redirects for common alternate paths */}
              <Route path="/audit" element={<Navigate to="/auditors" replace />} />
              <Route path="/book-demo" element={<Navigate to="/demo" replace />} />
              <Route path="/book-a-demo" element={<Navigate to="/demo" replace />} />
              <Route path="/trial" element={<Navigate to="/demo" replace />} />
              <Route path="/free-trial" element={<Navigate to="/demo" replace />} />
              <Route path="/signup" element={<Navigate to="/demo" replace />} />
              <Route path="/register" element={<Navigate to="/demo" replace />} />
              <Route path="/login" element={<Navigate to="/auth" replace />} />
              <Route path="/standards" element={<Navigate to="/rto-standards-2025" replace />} />
              <Route path="/standards-2025" element={<Navigate to="/rto-standards-2025" replace />} />
              <Route path="/2025-standards" element={<Navigate to="/rto-standards-2025" replace />} />
              <Route path="/articles" element={<Navigate to="/blog" replace />} />
              <Route path="/guides" element={<Navigate to="/resources" replace />} />
              <Route path="/help" element={<Navigate to="/faq" replace />} />
              <Route path="/about" element={<Navigate to="/product" replace />} />
              
              {/* Catch-all: Intelligent Intent Redirect */}
              <Route path="*" element={<IntentRedirect />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
